import { LLM_PROVIDERS } from "./llmRoutingPolicy";

const DEFAULT_OLLAMA_BASE_URL = "http://127.0.0.1:11434";
const DEFAULT_OLLAMA_MODEL = "llama3.1:8b";
const DEFAULT_OLLAMA_TIMEOUT_MS = 120000;
const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

const normalizeBaseUrl = (value) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  const withDefault = trimmed || DEFAULT_OLLAMA_BASE_URL;
  return withDefault.replace(/\/+$/, "");
};

const getOllamaBaseUrl = () =>
  normalizeBaseUrl(process.env.REACT_APP_OLLAMA_BASE_URL);

const getRuntimeHostname = () =>
  typeof window !== "undefined" ? String(window.location.hostname || "") : "";

const isLoopbackHostname = (value = "") =>
  LOOPBACK_HOSTNAMES.has(String(value || "").trim().toLowerCase());

const getBaseUrlHostname = (value = "") => {
  try {
    return new URL(normalizeBaseUrl(value)).hostname;
  } catch (error) {
    return "";
  }
};

const getOllamaModel = () =>
  (typeof process.env.REACT_APP_OLLAMA_MODEL === "string" &&
  process.env.REACT_APP_OLLAMA_MODEL.trim()
    ? process.env.REACT_APP_OLLAMA_MODEL.trim()
    : DEFAULT_OLLAMA_MODEL);

const getOllamaTimeoutMs = () => {
  const parsed = Number(process.env.REACT_APP_OLLAMA_TIMEOUT_MS);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_OLLAMA_TIMEOUT_MS;
  }
  return parsed;
};

const buildPrompt = ({ prompt, systemPrompt }) => {
  const userPrompt = typeof prompt === "string" ? prompt : String(prompt || "");
  const rolePrompt =
    typeof systemPrompt === "string" && systemPrompt.trim()
      ? `${systemPrompt.trim()}\n\n${userPrompt}`
      : userPrompt;
  return rolePrompt;
};

export const generateWithOllama = async ({
  prompt,
  systemPrompt = "",
  model = null,
  temperature = 0.6,
} = {}) => {
  const targetModel = model || getOllamaModel();
  const payload = {
    model: targetModel,
    prompt: buildPrompt({ prompt, systemPrompt }),
    stream: false,
    options: {
      temperature,
    },
  };

  const controller = new AbortController();
  const timeoutMs = getOllamaTimeoutMs();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${getOllamaBaseUrl()}/api/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(
        `Ollama request failed (${response.status}): ${errorBody || response.statusText}`
      );
    }

    const result = await response.json();
    const content = typeof result?.response === "string" ? result.response : "";
    if (!content.trim()) {
      throw new Error("Ollama returned an empty response.");
    }

    return {
      content,
      provider: LLM_PROVIDERS.OLLAMA,
      model: targetModel,
      raw: result,
    };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error(
        `Timed out waiting for Ollama after ${Math.round(timeoutMs / 1000)}s.`
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getOllamaConnectionInfo = () => ({
  baseUrl: getOllamaBaseUrl(),
  model: getOllamaModel(),
  timeoutMs: getOllamaTimeoutMs(),
});

export const shouldProbeOllamaFromBrowser = (baseUrl = getOllamaBaseUrl()) => {
  const runtimeHostname = getRuntimeHostname();
  if (!runtimeHostname || isLoopbackHostname(runtimeHostname)) {
    return true;
  }

  const targetHostname = getBaseUrlHostname(baseUrl);
  return !isLoopbackHostname(targetHostname);
};
